import { useEffect } from 'react';

const STYLE_ID = 'avenue-reveal-animations';

// Kept identical to the parent theme's reveal CSS (same STYLE_ID) so this repo's
// component overrides share one stylesheet with inherited components on the page
// — whichever injects first, the result is the same.
const REVEAL_CSS = `
/* ── Scroll-reveal animations ──
 * Elements start visible (SSG-safe). JS adds .reveal--hidden on mount,
 * then IntersectionObserver swaps to .reveal--visible on scroll.
 */

.reveal-fade-up.reveal--hidden {
  opacity: 0;
  transform: translateY(24px);
}
.reveal-fade-up.reveal--visible {
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
              transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
}

.reveal-scale.reveal--hidden {
  opacity: 0;
  transform: scale(0.92);
}
.reveal-scale.reveal--visible {
  opacity: 1;
  transform: scale(1);
  transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
              transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
}

.reveal-fade-in.reveal--hidden {
  opacity: 0;
}
.reveal-fade-in.reveal--visible {
  opacity: 1;
  transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
}

/* ── Accent line draw-in animations ── */

.accent-line {
  overflow: visible;
}
.accent-line line,
.accent-line path {
  stroke: var(--vibe-color-primary);
  stroke-width: 1;
  fill: none;
  stroke-dasharray: var(--line-length, 200);
  stroke-dashoffset: var(--line-length, 200);
  opacity: 0;
}
/* Direct reveal on the SVG itself */
.accent-line.reveal--visible line,
.accent-line.reveal--visible path {
  opacity: 1;
  stroke-dashoffset: 0;
  transition: stroke-dashoffset 1.2s cubic-bezier(0.22, 1, 0.36, 1),
              opacity 0.4s ease;
}
/* Inherited reveal from a parent container */
.reveal--visible .accent-line line,
.reveal--visible .accent-line path {
  opacity: 1;
  stroke-dashoffset: 0;
  transition: stroke-dashoffset 1.2s cubic-bezier(0.22, 1, 0.36, 1) 0.3s,
              opacity 0.4s ease 0.3s;
}
.reveal--visible .accent-line--delay-1 line,
.reveal--visible .accent-line--delay-1 path,
.accent-line--delay-1.reveal--visible line,
.accent-line--delay-1.reveal--visible path {
  transition-delay: 0.4s;
}
.reveal--visible .accent-line--delay-2 line,
.reveal--visible .accent-line--delay-2 path,
.accent-line--delay-2.reveal--visible line,
.accent-line--delay-2.reveal--visible path {
  transition-delay: 0.6s;
}
.reveal--visible .accent-line--delay-3 line,
.reveal--visible .accent-line--delay-3 path,
.accent-line--delay-3.reveal--visible line,
.accent-line--delay-3.reveal--visible path {
  transition-delay: 0.8s;
}
.accent-line--slow.reveal--visible line,
.accent-line--slow.reveal--visible path,
.reveal--visible .accent-line--slow line,
.reveal--visible .accent-line--slow path {
  transition-duration: 1.8s;
}
.accent-line--muted line,
.accent-line--muted path {
  stroke: var(--vibe-color-border);
}

/* ── Glow trace — SVG border sweep ── */

.glow-trace-wrap {
  position: relative;
}
.glow-trace-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 20;
  pointer-events: none;
  overflow: visible;
}
.glow-trace-svg rect {
  fill: none;
  stroke: var(--vibe-color-primary);
  stroke-width: 2;
  rx: 2;
  ry: 2;
  filter: drop-shadow(0 0 4px var(--vibe-color-primary))
         drop-shadow(0 0 8px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent));
}

@media (prefers-reduced-motion: reduce) {
  .reveal-fade-up.reveal--hidden,
  .reveal-scale.reveal--hidden,
  .reveal-fade-in.reveal--hidden {
    opacity: 1;
    transform: none;
  }
  .accent-line line,
  .accent-line path {
    stroke-dashoffset: 0;
    opacity: 1;
  }
  .glow-trace-svg rect {
    opacity: 0 !important;
    animation: none !important;
  }
}
`;

/**
 * Injects reveal animation CSS into <head> exactly once.
 * Call from any component that uses reveal classes.
 */
export function useRevealStyles(): void {
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = REVEAL_CSS;
    document.head.appendChild(style);
  }, []);
}
